/**
 * The words on the credits and change-list screens: the credits (docs/research/c64-original.md §1,
 * §9-10 and docs/research/music/README.md §2, §9 - only names the research supports), and the change
 * list built from CHANGELOG.md's releases, wrapped to the screen.
 */

import type { Release } from '../../app/version';
import { FONT_CHARS } from '../font8';
import { wrapText } from './text';

/** A line of text on a screen: its words and the colour role they take. */
export interface TextLine {
  text: string;
  role: 'head' | 'text' | 'dim' | 'accent';
  /** Drawn dim before `text` (a credit's job: "CODE"). */
  label?: string;
}

const head = (text: string): TextLine => ({ text, role: 'head' });
const credit = (label: string, text: string): TextLine => ({ label, text, role: 'text' });
const line = (text: string): TextLine => ({ text, role: 'text' });
const gap: TextLine = { text: '', role: 'text' };

export const CREDIT_LINES: readonly TextLine[] = [
  head('THE ORIGINAL'),
  credit('ARCADE', 'TAITO, 1986'),
  credit('DESIGN', 'FUKIO "MTJ" MITSUJI'),
  gap,
  head('THE COMMODORE 64 VERSION'),
  line('SOFTWARE CREATIONS FOR FIREBIRD, 1987'),
  credit('CODE', 'STEPHEN RUDDY'),
  credit('GRAPHICS', 'ANDREW R. THRELFALL'),
  credit('MUSIC', 'PETER CLARKE'),
  gap,
  head('THE MUSIC'),
  line('COMPOSED BY TADASHI KIMIJIMA (TAITO)'),
  line('ARRANGED FOR THE C64 BY PETER CLARKE'),
  gap,
  head('WITH THANKS TO'),
  credit('ZAIDKA', 'THE REBB64 DISASSEMBLY'),
  credit('GEON', 'THE BB CONSTRUCTION SET'),
  credit('HVSC', 'THE HIGH VOLTAGE SID COLLECTION'),
  gap,
  { text: 'THIS REMAKE: A PRIVATE FAN BUILD', role: 'accent' },
];

const FONT = new Set(FONT_CHARS);
/** Typographic characters a changelog may hold, as the font can show them. */
const SWAPS: Readonly<Record<string, string>> = {
  '‘': "'", '’': "'", '“': '"', '”': '"', '–': '-', '—': '-', '…': '...',
  '×': 'X', '[': '(', ']': ')', '*': '', '_': '', '`': "'", 'É': 'E',
};

/** Text in capitals, with anything the font lacks replaced by its nearest look-alike, or dropped. */
export function fontText(s: string): string {
  return [...s.toUpperCase()].map((ch) => SWAPS[ch] ?? (FONT.has(ch) ? ch : '')).join('');
}

/** Compares x.y.z versions, newer first. */
function newerFirst(a: Release, b: Release): number {
  const pa = a.version.split('.').map(Number);
  const pb = b.version.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const d = (pb[i] ?? 0) - (pa[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * The change list: each release's version and date, then its lines as bullets wrapped to `width`
 * characters with a hanging indent, a blank line between releases. Newest first.
 */
export function changeLines(releases: readonly Release[], width: number): TextLine[] {
  const out: TextLine[] = [];
  for (const r of [...releases].sort(newerFirst)) {
    if (out.length > 0) out.push(gap);
    out.push({ text: `V${r.version}   ${r.date}`, role: 'head' });
    for (const text of r.lines) {
      wrapText(fontText(text), width - 2).forEach((w, i) => out.push(line(`${i === 0 ? '-' : ' '} ${w}`)));
    }
  }
  if (out.length === 0) out.push({ text: 'NO CHANGES LISTED', role: 'dim' });
  return out;
}

/** Where the last version whose changes were seen is kept (every access guarded: storage may be blocked). */
export const SEEN_KEY = 'bubble-bobble.changesSeen';

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The version whose change list was last opened, or null. */
export function readSeen(): string | null {
  try {
    return storage()?.getItem(SEEN_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Remember that `version`'s changes have been seen; false if storage refused (it then lasts the visit). */
export function writeSeen(version: string): boolean {
  try {
    const s = storage();
    s?.setItem(SEEN_KEY, version);
    return s !== null;
  } catch {
    return false;
  }
}
